const groupPrefix = createApiRoute(ApiModule.DIETARY, ApiDataModel.DIET_DAYS)

export const apiDietDays = {
	fetchDietDays: async (dateFrom: Date, dateTo: Date, dietIds?: number[]): Promise<IDietDaysPreviewModel[]> => {
		const fetchPage = async (page: number, accumulated: IDietDaysPreview[]): Promise<IDietDaysPreview[]> => {
			const { data, meta } = await apiCommon<IDietDaysPreview[]>(`${groupPrefix}/for-preview`, {
				method: 'GET',
				...createQuery(
					{ page, perPage: 50 },
					{
						date_from: dateToFilterFormat(dateFrom),
						date_to: dateToFilterFormat(dateTo),
						diet_ids: dietIds,
					},
				),
			})

			const all: IDietDaysPreview[] = [...accumulated, ...(data ?? [])]

			if (!meta) return all

			const currentPage: number = meta.currentPage ?? 0
			const lastPage: number = meta.lastPage ?? 0

			return currentPage < lastPage ? fetchPage(page + 1, all) : all
		}

		const rawData: IDietDaysPreview[] = await fetchPage(1, [])

		return rawData.map(DietDaysPreview)
	},
	fetchDietMenuCalendar: async (diet_id?: number): Promise<IDietMenuCalendarModel> => {
		const { data } = await apiCommon<IDietMenuCalendar>(`${groupPrefix}/calendar`, {
			method: 'GET',
			params: { diet_id },
		})

		return DietMenuCalendar(data)
	},
	fetchDietMenu: async (date: string, diet_id?: number | null): Promise<IDietMenuModel | null> => {
		const { data } = await apiCommon<IDietMenu>(`${groupPrefix}/for-diet`, {
			method: 'GET',
			params: { date, diet_id },
		})

		if (!data) return null

		return DietMenu(data)
	},
	createOrUpdateDietMenu: async (payload: IDietMenuPayload): Promise<IDietMenuModel> => {
		const { data } = await apiCommon<IDietMenu>(`${groupPrefix}`, {
			method: 'POST',
			body: payload,
		})

		return DietMenu(data)
	},
	fetchDietMenuEditHistory: async (
		dietDayID: number,
		search: IApiSearch,
		{ page, perPage = 25 }: IApiPagination,
		filters?: IApiFilters,
	): Promise<IApiResponseModel<IDietaryActivityBusinessEventModel[]>> => {
		const searchFilter = createSearchQuery(search.query, search.type)
		const { data, meta } = await apiCommon<IDietaryActivityBusinessEvent[]>(`${groupPrefix}/${dietDayID}/logs`, {
			method: 'GET',
			...createQuery({ page, perPage }, { ...searchFilter, ...filters }),
		})

		return { data: data.map(DietaryActivityBusinessEvent), meta }
	},
	fetchDietMenuEditableStatus: async (date: string, dietID: number | undefined) => {
		const response = await apiCommon<any>(`${groupPrefix}/for-diet/edit`, {
			method: 'POST',
			params: { date, diet_id: dietID },
		})
		return { isLocked: response.is_locked }
	},
	unlockDietMenuEditableStatus: async (date: string, dietID: number) => {
		const { data } = await apiCommon<boolean>(`${groupPrefix}/for-diet/unlock`, {
			method: 'POST',
			params: { date, diet_id: dietID },
		})
		return data
	},
	duplicateDietMenu: async (payload: IDietMenuDuplicatePayload): Promise<IDietDaysDuplicateResponseModel> => {
		const { data } = await apiCommon<IDietDaysDuplicateResponse>(`${groupPrefix}/duplicate`, {
			method: 'POST',
			body: payload,
		})

		return DietDaysDuplicateResponse(data)
	},
	generateDietDaysDocument: async (
		dateFrom: Date,
		dateTo: Date,
		diet_ids?: number[],
		type: 'xlsx' | 'csv' = 'xlsx',
	) => {
		const response = await apiCommonRaw<BlobPart>(`${groupPrefix}/export-excel`, {
			method: 'GET',
			...createQuery(undefined, undefined, undefined, {
				date_from: dateToFilterFormat(dateFrom),
				date_to: dateToFilterFormat(dateTo),
				diet_ids,
				type,
			}),
			responseType: 'blob',
		})

		downloadFileFromResponse(response, `${String(dateFrom)}-${String(dateTo)}-diet-days.${type}`)
	},
}
